import { supabase } from './supabase'

// Chiave pubblica VAPID: non è un segreto, va bene che sia nel codice frontend
// (è pensata per essere distribuita — solo la chiave privata va protetta,
// e quella vive solo come secret della edge function).
const VAPID_PUBLIC_KEY = 'BG8LAXJr2UhbJ66WHBaoFK3RU1laQO-xkcri7EibMNz94dVVSaSOxbB0cFHuP5at5j3wnifJmJaUNmdreQCtkDs'

const PERCORSO_SW = '/push-sw.js'

function base64UrlAUint8Array(base64Url) {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)))
}

export function pushSupportato() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * iPhone e iPad: le notifiche web esistono solo dopo aver aggiunto l'app alla
 * schermata Home. In Safari PushManager non c'è affatto, quindi pushSupportato()
 * è falso e la scheda delle notifiche spariva dal profilo senza spiegazioni:
 * l'utente non poteva sapere né che la funzione esiste né come ottenerla.
 */
export function serveInstallazioneIOS() {
  if (typeof navigator === 'undefined') return false
  if (pushSupportato()) return false
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const giaInstallata = navigator.standalone === true ||
    (typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  return iOS && !giaInstallata
}

// Stato attuale del permesso browser: 'granted' | 'denied' | 'default'
export function statoPermessoPush() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'non-supportato'
  return Notification.permission
}

/** L'iscrizione viva su QUESTO browser, se c'è. */
async function iscrizioneSulDispositivo() {
  try {
    const registrazione = await navigator.serviceWorker.getRegistration(PERCORSO_SW)
    if (!registrazione) return null
    return (await registrazione.pushManager.getSubscription()) || null
  } catch {
    return null
  }
}

/** La riga corrispondente nel database, se c'è. È la metà che conta davvero:
 *  senza di essa il server non ha modo di raggiungere il dispositivo. */
async function iscrizioneNelDatabase(utenteId, endpoint) {
  if (!utenteId || !endpoint) return false
  try {
    const { data, error } = await supabase.from('push_subscriptions')
      .select('id').eq('endpoint', endpoint).eq('utente_id', utenteId).maybeSingle()
    if (error) return false
    return !!data
  } catch {
    return false
  }
}

/**
 * Scrive l'iscrizione nel database tramite registra_dispositivo_push.
 *
 * Non si scrive più direttamente sulla tabella con upsert(onConflict: 'endpoint'):
 * in SQL diventa INSERT ... ON CONFLICT DO UPDATE e, con RLS attiva, il ramo
 * DO UPDATE richiede una policy UPDATE che non esisteva. Risultato: la prima
 * attivazione passava, ogni riattivazione dello stesso dispositivo veniva
 * rifiutata con 42501 e l'utente leggeva solo "Non è stato possibile attivare
 * le notifiche". La funzione gestisce anche il dispositivo condiviso: se su quel
 * telefono prima c'era un altro account, l'endpoint passa all'utente corrente.
 */
async function salvaIscrizione(iscrizione) {
  let sub
  try { sub = iscrizione.toJSON() } catch { return { ok: false, motivo: 'iscrizione-illeggibile' } }
  const chiavi = (sub && sub.keys) || {}
  if (!sub?.endpoint || !chiavi.p256dh || !chiavi.auth) {
    return { ok: false, motivo: 'iscrizione-incompleta' }
  }
  const { error } = await supabase.rpc('registra_dispositivo_push', {
    p_endpoint: sub.endpoint,
    p_p256dh: chiavi.p256dh,
    p_auth: chiavi.auth,
  })
  if (error) return { ok: false, motivo: 'errore-salvataggio', dettaglio: error.message }
  return { ok: true }
}

/** Attende che il worker appena registrato sia attivo.
 *  Non si usa navigator.serviceWorker.ready: quella promessa riguarda il worker
 *  che controlla la pagina — può essere un altro, o nessuno, e restare appesa
 *  per sempre bloccando l'interruttore in stato di attesa. */
function attendiAttivazione(registrazione) {
  return new Promise((risolvi) => {
    if (registrazione.active) return risolvi()
    const inArrivo = registrazione.installing || registrazione.waiting
    if (!inArrivo) return risolvi()
    const controlla = () => {
      if (inArrivo.state === 'activated' || inArrivo.state === 'redundant') {
        inArrivo.removeEventListener('statechange', controlla)
        risolvi()
      }
    }
    inArrivo.addEventListener('statechange', controlla)
    // Rete lenta o worker che non si attiva: si prosegue comunque dopo 5 secondi.
    // Meglio un subscribe che magari fallisce (e lo si dice) di un'attesa infinita.
    setTimeout(risolvi, 5000)
  })
}

/**
 * Attiva le notifiche su questo dispositivo. L'utente è quello autenticato:
 * non serve passarne l'id, lo ricava il database da auth.uid().
 * Restituisce { ok: true } oppure { ok: false, motivo, dettaglio }.
 */
export async function abilitaNotifichePush() {
  if (!pushSupportato()) return { ok: false, motivo: 'non-supportato' }
  try {
    const permesso = await Notification.requestPermission()
    if (permesso !== 'granted') return { ok: false, motivo: 'permesso-negato' }

    const registrazione = await navigator.serviceWorker.register(PERCORSO_SW)
    await attendiAttivazione(registrazione)

    let iscrizione = await registrazione.pushManager.getSubscription()
    if (!iscrizione) {
      iscrizione = await registrazione.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlAUint8Array(VAPID_PUBLIC_KEY),
      })
    }
    return await salvaIscrizione(iscrizione)
  } catch (e) {
    // Prima non c'era alcun try/catch: un'eccezione qui (worker rimosso nel
    // frattempo, subscribe rifiutato dal browser) risaliva fino al componente,
    // che non la catturava — l'interruttore restava bloccato "in caricamento"
    // e all'utente non compariva nessun messaggio.
    return { ok: false, motivo: 'errore-browser', dettaglio: e?.message || String(e) }
  }
}

export async function disabilitaNotifichePush(utenteId) {
  if (!pushSupportato()) return { ok: true }
  try {
    const iscrizione = await iscrizioneSulDispositivo()
    if (iscrizione) {
      // Prima si toglie la riga dal database, poi si disiscrive il browser:
      // nell'ordine inverso, se la cancellazione fallisse, il server
      // continuerebbe a inviare verso un endpoint ormai morto.
      //
      // Qui basta la policy DELETE della tabella (utente_id = auth.uid()):
      // si arriva a disattivare solo se l'interruttore risultava acceso, e
      // risulta acceso solo quando la riga è di questo utente. Il caso del
      // telefono passato da un account all'altro lo risolve già
      // registra_dispositivo_push in attivazione, e una riga rimasta orfana
      // la elimina send-push al primo invio che torna 410.
      await supabase.from('push_subscriptions').delete()
        .eq('endpoint', iscrizione.endpoint).eq('utente_id', utenteId)
      await iscrizione.unsubscribe()
    } else if (utenteId) {
      // Nessuna iscrizione attiva sul dispositivo: rimuovi comunque eventuali
      // righe residue dell'utente.
      await supabase.from('push_subscriptions').delete().eq('utente_id', utenteId)
    }
  } catch {
    // Anche in caso di errore lato browser, la disattivazione non deve bloccarsi.
  }
  return { ok: true }
}

/**
 * Stato VERO delle notifiche su questo dispositivo: browser iscritto *e* riga
 * nel database. Prima si guardava solo il browser, e bastava che la riga nel
 * database mancasse (pulizia, ripristino, iscrizione rinnovata dal browser)
 * perché l'interruttore restasse verde mentre non arrivava più nulla.
 */
export async function notifichePushAttive(utenteId) {
  if (!pushSupportato() || statoPermessoPush() !== 'granted') return false
  const iscrizione = await iscrizioneSulDispositivo()
  if (!iscrizione) return false
  return await iscrizioneNelDatabase(utenteId, iscrizione.endpoint)
}

/**
 * Istruzioni per riabilitare le notifiche quando il browser le ha bloccate.
 * "Abilitale dalle impostazioni del sito" non basta: su un telefono quelle
 * impostazioni non sono dove uno se le aspetta, e l'utente resta fermo.
 */
export function comeSbloccareNotifiche() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || ''
  const iOS = /iPad|iPhone|iPod/.test(ua) ||
    (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (iOS) return 'Apri Impostazioni di iPhone → Notifiche → FitProgress e consenti le notifiche, poi riapri l\'app.'
  if (/Android/.test(ua)) return 'Tocca il lucchetto accanto all\'indirizzo (oppure ⋮ → Informazioni sito) → Notifiche → Consenti, poi ricarica la pagina.'
  return 'Clicca il lucchetto accanto all\'indirizzo → Notifiche → Consenti, poi ricarica la pagina.'
}

/**
 * Fotografia dello stato delle notifiche su questo dispositivo.
 * Serve quando qualcosa non funziona: invece di un interruttore muto, la
 * scheda del profilo può dire esattamente quale pezzo manca — ed è una riga
 * che si può leggere a voce a chi sta diagnosticando il problema.
 */
export async function diagnosticaPush(utenteId) {
  const supporto = pushSupportato()
  const permesso = statoPermessoPush()
  let iscrizioneBrowser = false
  let rigaDatabase = false
  if (supporto && permesso === 'granted') {
    const iscrizione = await iscrizioneSulDispositivo()
    iscrizioneBrowser = !!iscrizione
    if (iscrizione) rigaDatabase = await iscrizioneNelDatabase(utenteId, iscrizione.endpoint)
  }
  return {
    supporto,
    permesso,
    iscrizioneBrowser,
    rigaDatabase,
    iosDaInstallare: serveInstallazioneIOS(),
    funzionante: supporto && permesso === 'granted' && iscrizioneBrowser && rigaDatabase,
    riassunto: 'supporto=' + (supporto ? 'sì' : 'no') +
      ' · permesso=' + permesso +
      ' · iscrizione=' + (iscrizioneBrowser ? 'sì' : 'no') +
      ' · database=' + (rigaDatabase ? 'sì' : 'no'),
  }
}

/**
 * Rimette d'accordo dispositivo e database senza chiedere niente all'utente:
 * se il browser è ancora iscritto ma la riga nel database manca, la riscrive.
 * Va chiamata all'avvio dell'app: è ciò che ripara da sola le iscrizioni
 * rimaste a metà, invece di lasciare notifiche silenziosamente morte.
 * Restituisce lo stato vero, da usare per l'interruttore.
 */
export async function sincronizzaPush(utenteId) {
  if (!utenteId || !pushSupportato() || statoPermessoPush() !== 'granted') return false
  const iscrizione = await iscrizioneSulDispositivo()
  if (!iscrizione) return false
  if (await iscrizioneNelDatabase(utenteId, iscrizione.endpoint)) return true
  const esito = await salvaIscrizione(iscrizione)
  return !!esito.ok
}
